import React, { Component } from 'react';
import Select, { StylesConfig, SingleValue, ActionMeta, GroupBase } from 'react-select';

import { themes } from 'kb-shared';
import styled from 'styled-components';
import { isUndefinedOrEmptyString } from 'utilities/isUndefinedOrEmptyString';

import { Label, RequiredIndicatorLabel } from './TextInput/NewTextInput.styles';

const { colors, deprecatedColors } = themes;

type ContainerInterface = {
  fullMode?: boolean;
  disableHeightLimit: boolean;
};

const Container = styled.div<ContainerInterface>`
  display: block;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  height: ${({ disableHeightLimit }) => (disableHeightLimit ? 'auto' : '55px')};
  max-height: ${({ disableHeightLimit }) => (disableHeightLimit ? 'unset' : '55px')};
  max-width: ${({ fullMode }) => (fullMode ? '100%' : '340px')};
  @media ${themes.queries.mobile} {
    max-width: 100%;
  }
  background: white;
`;

type Props<T> = {
  options: T[];
  label: string;
  placeholder: string;
  selectedOption: T | null;
  value?: string;
  type?: string;
  error?: boolean;
  className?: string;
  isNew?: boolean;
  name?: string;
  disabled?: boolean;
  onSelect: (item: T) => void;
  extractTitle: (item: T | null) => string;
  isRequired?: boolean;
  fullMode?: boolean;
  maxHeight?: number;
  ariaLabelledby?: string;
  externalAriaLabelledby?: string;
  inputId?: string;
  disableHeightLimit?: boolean;
};

export interface SelectItem<T = unknown> {
  original: T;
  label: string;
  value: string;
}

export interface SelectAction {
  action: string;
  name?: string;
}

export default class SelectInput<T> extends Component<Props<T>> {
  static defaultProps: Props<unknown> = {
    options: [],
    label: '',
    placeholder: '',
    selectedOption: null,
    isNew: false,
    name: '',
    onSelect: () => {},
    extractTitle: () => '',
    ariaLabelledby: '',
    externalAriaLabelledby: ''
  };

  customStyles: StylesConfig<SelectItem<T>, false, GroupBase<SelectItem<T>>> = {
    control: (provided, { isFocused }) => ({
      ...provided,
      backgroundColor: 'transparent',
      borderRadius: '0',
      border: 'none',
      cursor: 'pointer',
      boxShadow: 'none',
      borderBottom: `solid 1px ${isFocused ? colors.yellow.primary : 'black'}`,
      '&:hover': {
        borderBottom: `solid 1px ${colors.yellow.primary}`
      }
    }),
    option: (provided, { isFocused, isSelected }) => {
      const backgroundColor = getBackgroundColor(isSelected, isFocused);

      return {
        ...provided,
        backgroundColor,
        fontSize: '24px',
        color: colors.neutral.primaryNavy,
        '&:hover': {
          cursor: 'pointer'
        }
      };
    },
    dropdownIndicator: (provided, { isFocused }) => ({
      ...provided,
      color: isFocused ? colors.yellow.primary : undefined
    }),
    indicatorSeparator: provided => ({
      ...provided,
      display: 'none'
    }),
    placeholder: provided => ({
      ...provided,
      fontDisplay: 'swap',
      fontSize: '24px',
      textAlign: 'left',
      color: deprecatedColors.gray
    }),
    valueContainer: provided => ({
      ...provided,
      fontDisplay: 'swap',
      fontSize: '24px'
    }),
    menu: provided => ({
      ...provided,
      zIndex: '101'
    })
  };

  newStyles: StylesConfig<SelectItem<T>, false, GroupBase<SelectItem<T>>> = {
    control: (provided, { isFocused }) => ({
      ...provided,
      backgroundColor: 'transparent',
      borderRadius: '4px',
      cursor: 'pointer',
      boxShadow: 'none',
      border: isFocused
        ? `solid 2px ${colors.yellow.primary}`
        : `solid 1px ${deprecatedColors.gray}`,
      '&:hover': {
        backgroundColor: colors.yellow.primary
      },
      padding: '18px'
    }),
    option: (provided, { isFocused, isSelected }) => {
      const backgroundColor = getBackgroundColor(isSelected, isFocused);

      return {
        ...provided,
        backgroundColor,
        fontSize: '16px',
        borderBottom: `solid 1px ${deprecatedColors.gray}`,
        borderRadius: '4px',
        padding: '18px',
        fontWeight: 'bolder',
        color: colors.neutral.primaryNavy,
        margin: '0'
      };
    },
    indicatorSeparator: provided => ({
      ...provided,
      display: 'none'
    }),
    dropdownIndicator: (provided, { isFocused }) => ({
      ...provided,
      color: isFocused ? colors.yellow.primary : undefined
    }),
    valueContainer: provided => ({
      ...provided,
      fontDisplay: 'swap',
      fontSize: '16px',
      fontWeight: 'bolder'
    }),
    menu: provided => ({
      ...provided,
      position: 'relative',
      zIndex: '101'
    })
  };

  render() {
    const { label, selectedOption, name, isRequired, fullMode, inputId, disableHeightLimit } =
      this.props;
    const value = this.props.extractTitle(selectedOption);

    const options = this.props.options.map(item => {
      const title = this.props.extractTitle(item);

      return {
        label: title,
        value: title,
        original: item
      };
    });

    const renderSelect = () => {
      const props: {
        [key: string]: string;
      } = {
        'aria-labelledby': `${this.props.ariaLabelledby} ${this.props.externalAriaLabelledby}`
      };

      Object.keys(props).forEach(
        (key: string) => isUndefinedOrEmptyString(props[key]) && delete props[key]
      );

      return (
        <Select
          name={name}
          options={options}
          isDisabled={this.props.disabled}
          styles={this.props.isNew ? this.newStyles : this.customStyles}
          onChange={(
            newValue: SingleValue<SelectItem<T>>,
            _actionMeta: ActionMeta<SelectItem<T>>
          ) => this.props.onSelect(newValue?.original as T)}
          placeholder={this.props.placeholder}
          className={this.props.className}
          isSearchable={false}
          value={
            value !== '' && this.props.selectedOption
              ? { label: value, value: value, original: this.props.selectedOption }
              : null
          }
          maxMenuHeight={this.props.maxHeight ?? 250}
          inputId={this.props.inputId}
          {...props}
        />
      );
    };

    return (
      <Container fullMode={fullMode} disableHeightLimit={disableHeightLimit ?? false}>
        <Label id={this.props.ariaLabelledby} visible={true} htmlFor={inputId}>
          {label}
        </Label>
        {isRequired && <RequiredIndicatorLabel>{'*'}</RequiredIndicatorLabel>}
        {renderSelect()}
      </Container>
    );
  }
}

const getBackgroundColor = (isSelected: boolean, isFocused: boolean) => {
  if (isSelected) {
    return colors.yellow.primary;
  } else if (isFocused) {
    return colors.yellow.lighterYellow;
  }
  return 'transparent';
};
